import { inject } from "@angular/core";
import { patchState, signalStoreFeature, type, withMethods } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";
import { IProviderServiceState } from "./provider-service.state";
import { exhaustMap, pipe, tap } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { ProviderServiceService } from "../../services/provider-service/provider-service.service";
import { ProviderService, ProviderServicePayload } from "../../models/provider/provider-service.model";
import { withProviderServiceStateMethods } from "./provider-service.methods.state";
import { NotificationService } from "../../services/notification/notification.service";

export const withProviderServiceApiMethods = signalStoreFeature(
    { state: type<IProviderServiceState>() },
    withProviderServiceStateMethods,
    withMethods((store) => {
        const providerServiceService = inject(ProviderServiceService);
        const notificationService = inject(NotificationService);

        const loadProviderServices = rxMethod<string>(
            pipe(
                tap((providerGuid) => patchState(store, { loading: true, error: null, providerGuid })),
                exhaustMap((providerGuid) =>
                    providerServiceService.getByProvider(providerGuid).pipe(
                        tapResponse({
                            next: (providerServices: ProviderService[]) => store.setProviderServices(providerServices),
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const createProviderService = rxMethod<{ providerGuid: string; payload: ProviderServicePayload }>(
            pipe(
                tap(() => patchState(store, { saving: true, error: null })),
                exhaustMap(({ providerGuid, payload }) =>
                    providerServiceService.create(providerGuid, payload).pipe(
                        tapResponse({
                            next: (providerService: ProviderService) => {
                                patchState(store, { saving: false });
                                notificationService.notify('success', 'Success', 'Service created successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const updateProviderService = rxMethod<{ providerGuid: string; providerServiceGuid: string; payload: Partial<ProviderServicePayload> }>(
            pipe(
                tap(() => patchState(store, { saving: true, error: null })),
                exhaustMap(({ providerGuid, providerServiceGuid, payload }) =>
                    providerServiceService.update(providerGuid, providerServiceGuid, payload).pipe(
                        tapResponse({
                            next: (providerService: ProviderService) => {
                                patchState(store, { saving: false });
                                notificationService.notify('success', 'Success', 'Service updated successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const deleteProviderService = rxMethod<{ providerGuid: string; providerServiceGuid: string }>(
            pipe(
                tap(() => patchState(store, { deleting: true, error: null })),
                exhaustMap(({ providerGuid, providerServiceGuid }) =>
                    providerServiceService.delete(providerGuid, providerServiceGuid).pipe(
                        tapResponse({
                            next: () => {
                                store.removeProviderService(providerServiceGuid);
                                notificationService.notify('success', 'Success', 'Service deleted successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const toggleProviderServiceActive = rxMethod<{ providerGuid: string; providerServiceGuid: string }>(
            pipe(
                exhaustMap(({ providerGuid, providerServiceGuid }) =>
                    providerServiceService.toggleActive(providerGuid, providerServiceGuid).pipe(
                        tapResponse({
                            next: (providerService: ProviderService) => store.replaceProviderService(providerService),
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const loadProviderServiceByGuid = rxMethod<{ providerGuid: string; providerServiceGuid: string }>(
            pipe(
                tap(() => patchState(store, { loading: true, error: null })),
                exhaustMap(({ providerGuid, providerServiceGuid }) =>
                    providerServiceService.getByGuid(providerGuid, providerServiceGuid).pipe(
                        tapResponse({
                            next: (providerService: ProviderService) => store.setCurrentProviderService(providerService),
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        return { loadProviderServices, createProviderService, updateProviderService, deleteProviderService, toggleProviderServiceActive, loadProviderServiceByGuid };
    })
);
